// client/src/coms/views/canvas_parts/party/partyDrawingModel.js
// Client side of the party drawing format (server/utils/party/partyDrawing.js): drawings hold the writers'
// pictures from the Canvas editor (and, for older games, stroke operations), plus the renderer shared by
// live viewers, galleries and animation playback. Coordinates live on a 1000 x 750 board.

export const BOARD = { w: 1000, h: 750 };
export const PALETTE = [
  '#000000', '#ffffff', '#7f7f7f', '#c3c3c3', '#e53935', '#ff8a80', '#fb8c00', '#ffcc80',
  '#fdd835', '#fff59d', '#43a047', '#a5d6a7', '#1e88e5', '#90caf9', '#3949ab', '#8e24aa',
  '#ce93d8', '#6d4c41', '#bcaaa4', '#00acc1', '#f06292', '#2e7d32', '#ffd54f', '#263238',
];
export const SIZES = [2, 4, 8, 14, 24, 40];
export const ALL_TOOLS = ['pen', 'brush', 'eraser', 'fill', 'line', 'rect', 'ellipse'];
const STROKE_TOOLS = new Set(['pen', 'brush', 'eraser']);

export function clampPoint(x, y, rules) {
  let px = Math.max(0, Math.min(BOARD.w, Math.round(x)));
  let py = Math.max(0, Math.min(BOARD.h, Math.round(y)));
  if (rules && rules.mirror) px = BOARD.w - px;
  if (rules && rules.bounds) {
    px = Math.max(rules.bounds.x0, Math.min(rules.bounds.x1, px));
    py = Math.max(rules.bounds.y0, Math.min(rules.bounds.y1, py));
  }
  return [px, py];
}

export function isStraightStroke(points) {
  if (points.length < 6) return false;
  const x0 = points[0];
  const y0 = points[1];
  const x1 = points[points.length - 3];
  const y1 = points[points.length - 2];
  const length = Math.hypot(x1 - x0, y1 - y0);
  if (length < 60) return false;
  let worst = 0;
  for (let i = 3; i < points.length - 3; i += 3) {
    const d = Math.abs((y1 - y0) * points[i] - (x1 - x0) * points[i + 1] + x1 * y0 - y1 * x0) / length;
    if (d > worst) worst = d;
  }
  return worst < length * 0.03;
}

// The tool, colour and size forced by a "tool roulette" schedule at server time `t`.
export function scheduledEntry(rules, t) {
  if (!rules || !Array.isArray(rules.schedule) || !rules.schedule.length) return null;
  let current = null;
  rules.schedule.forEach((entry) => {
    if (t >= entry.at) current = entry;
  });
  return current;
}

// A mutable mirror of one drawing. Applies relayed server operations in order.
export function createModel(snapshot) {
  return {
    id: snapshot ? snapshot.id : '',
    seq: snapshot ? snapshot.seq : 0,
    base: snapshot && snapshot.base ? snapshot.base : null,
    basePictures: snapshot && Array.isArray(snapshot.basePictures) ? snapshot.basePictures : [],
    items: snapshot && Array.isArray(snapshot.items) ? snapshot.items.map((item) => ({ ...item, p: item.p.slice() })) : [],
    pictures: snapshot && Array.isArray(snapshot.pictures) ? snapshot.pictures.slice() : [],
    open: new Map(),
    redo: new Map(),
    version: 0,
  };
}

// A writer's newer picture replaces their older one; pictures keep the order writers first appeared.
export function applyPicture(model, picture) {
  if (!model || !picture || !picture.src) return false;
  const index = model.pictures.findIndex((entry) => entry.u === picture.u);
  if (index >= 0) {
    if (Number(model.pictures[index].seq) >= Number(picture.seq)) return false;
    model.pictures[index] = picture;
  } else {
    model.pictures.push(picture);
  }
  model.version += 1;
  return true;
}

function lastIndexBy(model, userId) {
  for (let i = model.items.length - 1; i >= 0; i -= 1) {
    if (model.items[i].u === userId) return i;
  }
  return -1;
}

export function applyRelayOp(model, op) {
  const u = op.u;
  if (op.o === 's') {
    model.items.push({ u, t: op.t, c: op.c, w: op.w, p: op.p.slice() });
    model.open.set(u, model.items.length - 1);
  } else if (op.o === 'a') {
    const index = model.open.get(u);
    if (index !== undefined && model.items[index]) model.items[index].p.push(...op.p);
  } else if (op.o === 'e') {
    model.open.delete(u);
  } else if (op.o === 'k') {
    model.items.push({ u, t: op.t, c: op.c, w: op.w, p: op.p.slice(), ...(op.f ? { f: 1 } : {}) });
  } else if (op.o === 'u') {
    model.open.delete(u);
    const index = lastIndexBy(model, u);
    if (index >= 0) model.items.splice(index, 1);
  } else if (op.o === 'r') {
    model.open.delete(u);
    if (op.item) model.items.push({ ...op.item, p: op.item.p.slice() });
  } else if (op.o === 'x') {
    model.open.delete(u);
    model.items.push({ u, t: 'clear', c: '', w: 0, p: [] });
  } else if (op.o === 'drop') {
    model.open.delete(u);
    if (model.items[op.i]) model.items.splice(op.i, 1);
  }
  model.version += 1;
}

// ---------------------------------------------------------------------------------------------- rendering
function hexToRgba(hex) {
  const value = parseInt(String(hex || '#000000').slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255, 255];
}

function floodFill(ctx, x, y, color) {
  const { width, height } = ctx.canvas;
  if (!width || !height) return;
  const px = Math.max(0, Math.min(width - 1, Math.floor(x)));
  const py = Math.max(0, Math.min(height - 1, Math.floor(y)));
  let image;
  try {
    image = ctx.getImageData(0, 0, width, height);
  } catch {
    return;
  }
  const data = image.data;
  const start = (py * width + px) * 4;
  const target = [data[start], data[start + 1], data[start + 2], data[start + 3]];
  const fill = hexToRgba(color);
  if (target.every((value, k) => Math.abs(value - fill[k]) < 4)) return;
  const tolerance = 48;
  const matches = (index) => Math.abs(data[index] - target[0]) <= tolerance
    && Math.abs(data[index + 1] - target[1]) <= tolerance
    && Math.abs(data[index + 2] - target[2]) <= tolerance
    && Math.abs(data[index + 3] - target[3]) <= tolerance;
  const seen = new Uint8Array(width * height);
  const stack = [px, py];
  while (stack.length) {
    const sy = stack.pop();
    let sx = stack.pop();
    while (sx >= 0 && matches((sy * width + sx) * 4) && !seen[sy * width + sx]) sx -= 1;
    sx += 1;
    let up = false;
    let down = false;
    while (sx < width && matches((sy * width + sx) * 4) && !seen[sy * width + sx]) {
      const index = (sy * width + sx) * 4;
      seen[sy * width + sx] = 1;
      data[index] = fill[0];
      data[index + 1] = fill[1];
      data[index + 2] = fill[2];
      data[index + 3] = 255;
      if (sy > 0) {
        const above = ((sy - 1) * width + sx) * 4;
        if (!up && matches(above) && !seen[(sy - 1) * width + sx]) {
          stack.push(sx, sy - 1);
          up = true;
        } else if (up && !matches(above)) up = false;
      }
      if (sy < height - 1) {
        const below = ((sy + 1) * width + sx) * 4;
        if (!down && matches(below) && !seen[(sy + 1) * width + sx]) {
          stack.push(sx, sy + 1);
          down = true;
        } else if (down && !matches(below)) down = false;
      }
      sx += 1;
    }
  }
  ctx.putImageData(image, 0, 0);
}

function brushWidth(item, pressure) {
  if (item.t !== 'brush') return item.w;
  return Math.max(1, item.w * (0.35 + (pressure / 100) * 1.0));
}

// Draws one item; `scale` maps board units to canvas pixels. On transparent layers the eraser cuts through.
export function drawItem(ctx, item, scale, { transparent = false } = {}) {
  const p = item.p || [];
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (item.t === 'clear') {
    if (transparent) ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }
    ctx.restore();
    return;
  }
  const erasing = item.t === 'eraser';
  if (erasing && transparent) ctx.globalCompositeOperation = 'destination-out';
  const color = erasing ? '#ffffff' : item.c;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  if (STROKE_TOOLS.has(item.t)) {
    if (p.length === 3) {
      ctx.beginPath();
      ctx.arc(p[0] * scale, p[1] * scale, (brushWidth(item, p[2]) * scale) / 2, 0, Math.PI * 2);
      ctx.fill();
    } else if (item.t === 'brush') {
      for (let i = 3; i < p.length; i += 3) {
        ctx.lineWidth = brushWidth(item, (p[i - 1] + p[i + 2]) / 2) * scale;
        ctx.beginPath();
        ctx.moveTo(p[i - 3] * scale, p[i - 2] * scale);
        ctx.lineTo(p[i] * scale, p[i + 1] * scale);
        ctx.stroke();
      }
    } else {
      ctx.lineWidth = item.w * scale;
      ctx.beginPath();
      ctx.moveTo(p[0] * scale, p[1] * scale);
      // Midpoint smoothing for a less jagged pen.
      for (let i = 3; i < p.length - 3; i += 3) {
        const mx = ((p[i] + p[i + 3]) / 2) * scale;
        const my = ((p[i + 1] + p[i + 4]) / 2) * scale;
        ctx.quadraticCurveTo(p[i] * scale, p[i + 1] * scale, mx, my);
      }
      ctx.lineTo(p[p.length - 3] * scale, p[p.length - 2] * scale);
      ctx.stroke();
    }
  } else if (item.t === 'line') {
    ctx.lineWidth = item.w * scale;
    ctx.beginPath();
    ctx.moveTo(p[0] * scale, p[1] * scale);
    ctx.lineTo(p[3] * scale, p[4] * scale);
    ctx.stroke();
  } else if (item.t === 'rect' || item.t === 'ellipse') {
    ctx.lineWidth = item.w * scale;
    const x = Math.min(p[0], p[3]) * scale;
    const y = Math.min(p[1], p[4]) * scale;
    const w = Math.abs(p[3] - p[0]) * scale;
    const h = Math.abs(p[4] - p[1]) * scale;
    ctx.beginPath();
    if (item.t === 'rect') ctx.rect(x, y, w, h);
    else ctx.ellipse(x + w / 2, y + h / 2, Math.max(0.5, w / 2), Math.max(0.5, h / 2), 0, 0, Math.PI * 2);
    if (item.f) ctx.fill();
    else ctx.stroke();
  } else if (item.t === 'poly') {
    ctx.lineWidth = (item.w || 8) * scale;
    ctx.beginPath();
    for (let i = 0; i < p.length; i += 2) {
      if (i === 0) ctx.moveTo(p[i] * scale, p[i + 1] * scale);
      else ctx.lineTo(p[i] * scale, p[i + 1] * scale);
    }
    ctx.closePath();
    if (item.f) ctx.fill();
    else ctx.stroke();
  } else if (item.t === 'fill') {
    ctx.restore();
    floodFill(ctx, p[0] * scale, p[1] * scale, item.c);
    return;
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------------------------- pictures
// Decoded pictures, shared by every view; a view asks again when the image it needs has loaded.
const imageCache = new Map();

function imageFor(src, onLoad) {
  if (!src || typeof Image === 'undefined') return null;
  let entry = imageCache.get(src);
  if (!entry) {
    entry = { image: new Image(), loaded: false, failed: false, waiting: new Set() };
    imageCache.set(src, entry);
    if (imageCache.size > 400) imageCache.delete(imageCache.keys().next().value);
    const current = entry;
    current.image.onload = () => {
      current.loaded = true;
      current.waiting.forEach((fn) => fn());
      current.waiting.clear();
    };
    current.image.onerror = () => {
      // Waiters still hear back, so a missing picture never stalls an export; views simply skip it.
      current.failed = true;
      current.waiting.forEach((fn) => fn());
      current.waiting.clear();
    };
    current.image.src = src;
  }
  if (entry.loaded) return entry.image;
  if (onLoad && !entry.failed) entry.waiting.add(onLoad);
  return null;
}

function drawPictures(ctx, pictures, onImage) {
  const { width, height } = ctx.canvas;
  (pictures || []).forEach((picture) => {
    const image = imageFor(picture.src, onImage);
    if (!image) return;
    const x = ((Number(picture.x) || 0) / BOARD.w) * width;
    const y = ((Number(picture.y) || 0) / BOARD.h) * height;
    const w = ((Number(picture.w) || BOARD.w) / BOARD.w) * width;
    const h = ((Number(picture.h) || BOARD.h) / BOARD.h) * height;
    ctx.drawImage(image, x, y, w, h);
  });
}

// Resolves once every picture in the models has loaded (for exports that draw many frames at once).
export function loadModelPictures(models) {
  const sources = [];
  (models || []).forEach((model) => {
    if (!model) return;
    [...(model.basePictures || []), ...(model.pictures || [])].forEach((picture) => sources.push(picture.src));
  });
  return Promise.all(sources.map((src) => new Promise((resolve) => {
    if (imageFor(src, resolve)) resolve();
    else if (!imageCache.get(src) || imageCache.get(src).failed) resolve();
  })));
}

// Renders a whole picture: white paper (unless transparent), optional base items, then the items.
export function renderItems(ctx, items, { base = null, transparent = false } = {}) {
  const { width, height } = ctx.canvas;
  const scale = width / BOARD.w;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, width, height);
  if (!transparent) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.restore();
  (base || []).forEach((item) => drawItem(ctx, item, scale, { transparent }));
  (items || []).forEach((item) => drawItem(ctx, item, scale, { transparent }));
}

// Renders one drawing model: paper, the part another player started, the strokes, then the writers'
// pictures. `onImage` is called when a picture that was still loading becomes available.
export function renderModel(ctx, model, { transparent = false, onImage = null } = {}) {
  renderItems(ctx, model ? model.base || [] : [], { transparent });
  if (!model) return;
  drawPictures(ctx, model.basePictures, onImage);
  const scale = ctx.canvas.width / BOARD.w;
  (model.items || []).forEach((item) => drawItem(ctx, item, scale, { transparent }));
  drawPictures(ctx, model.pictures, onImage);
}

// Renders several drawings stacked as layers (Half & Half), each on its own transparent layer.
export function renderLayers(ctx, layers, { onImage = null } = {}) {
  const { width, height } = ctx.canvas;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
  const scratch = document.createElement('canvas');
  scratch.width = width;
  scratch.height = height;
  const sctx = scratch.getContext('2d');
  layers.forEach((layer) => {
    if (!layer) return;
    renderModel(sctx, layer, { transparent: true, onImage });
    ctx.drawImage(scratch, 0, 0);
  });
}

export function countOwnStrokes(items, userId) {
  return items.filter((item) => item.u === userId && item.t !== 'clear' && item.t !== 'fill').length;
}
